import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HUD } from '@/game/hud';
import { createScene, type GameResult, type SessionState, type SceneController } from '@/game/scene';
import { startAmbient, stopAmbient } from '@/game/sounds';
import { completePlaySession, startLooplayAttempt, trackLooplayView, trackMatch } from '@/looplay';
type Phase = 'lobby' | 'howToPlay' | 'playing' | 'gameOver';
const initialSession: SessionState = {
  coins: 100,
  score: 0,
  timeRemaining: 120,
  power: 1,
  paused: false,
  fishCaught: 0,
  cannonCanFire: true,
};
const initialResult: GameResult = {
  finalScore: 0,
  fishCaught: 0,
  reason: 'time',
  success: true,
  elapsedSeconds: 0,
};
const overlayMotion = {
  initial: { opacity: 0, scale: 0.95, y: 12 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring' as const, stiffness: 260, damping: 24, duration: 0.3 } },
  exit: { opacity: 0, scale: 0.97, y: -8, transition: { duration: 0.18 } },
};
export function HomePage() {
  const [phase, setPhase] = useState<Phase>(() =>
    window.location.hash === '#playing' || window.location.search.includes('screen=playing') ? 'playing' : 'lobby',
  );
  const [state, setState] = useState<SessionState>(initialSession);
  const [result, setResult] = useState<GameResult>(initialResult);
  const canvasRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SceneController | null>(null);
  const sessionStartedAtRef = useRef<number | null>(null);
  useEffect(() => {
    void trackLooplayView();
  }, []);
  useEffect(() => {
    if (phase !== 'playing' || !canvasRef.current) return undefined;
    let cancelled = false;
    void createScene(canvasRef.current, {
      onStateChange: setState,
      onGameOver: (next) => {
        setResult(next);
        setPhase('gameOver');
        const durationSeconds = Math.max(0, Math.round(next.elapsedSeconds));
        void completePlaySession();
        void trackMatch(`reef-strike-${Date.now()}`, {
          durationSeconds,
          isCompleted: true,
          isWin: next.success,
        });
        sessionStartedAtRef.current = null;
      },
    }).then((scene) => {
      if (cancelled) {
        void scene.destroy();
      } else {
        sceneRef.current = scene;
        startAmbient();
      }
    }).catch((error: unknown) => {
      console.error('[Reef Strike] Scene initialization failed', error);
      setResult({ ...initialResult, success: false, reason: 'coins', finalScore: 0, fishCaught: 0, elapsedSeconds: 0 });
      setPhase('gameOver');
    });
    return () => {
      cancelled = true;
      stopAmbient();
      void sceneRef.current?.destroy();
      sceneRef.current = null;
    };
  }, [phase]);
  const start = () => {
    if (navigator.vibrate) navigator.vibrate(40);
    sessionStartedAtRef.current = Date.now();
    setState(initialSession);
    setResult(initialResult);
    setPhase('playing');
    window.history.replaceState(null, '', '#playing');
    void startLooplayAttempt();
  };
  const returnToLobby = () => {
    stopAmbient();
    sessionStartedAtRef.current = null;
    setPhase('lobby');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  };
  return (
    <main className="game-shell relative flex h-[100dvh] w-full flex-col overflow-hidden bg-[#090d16] text-white">
      <AnimatePresence>
        {phase === 'playing' && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative flex h-full min-h-0 flex-1 flex-col"
          >
            <div ref={canvasRef} className="relative min-h-0 flex-1 overflow-hidden" />
            <HUD
              state={state}
              onPause={() => state.paused ? sceneRef.current?.resume() : sceneRef.current?.pause()}
              onPower={(power) => sceneRef.current?.setPower(power)}
            />
          </motion.div>
        )}
        {phase === 'lobby' && (
          <motion.section
            key="lobby"
            {...overlayMotion}
            className="relative z-20 flex h-full w-full items-center justify-center px-6"
            style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
          >
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <span className="bubble-keyframe absolute left-12 top-20 h-5 w-5 rounded-full bg-cyan-200/30" />
              <span className="bubble-keyframe absolute right-16 top-40 h-8 w-8 rounded-full bg-amber-200/25 [animation-delay:1s]" />
              <span className="bubble-keyframe absolute bottom-32 left-20 h-3 w-3 rounded-full bg-white/30 [animation-delay:2s]" />
            </div>
            <div className="pointer-events-auto w-full max-w-sm rounded-3xl border border-white/15 bg-slate-950/75 p-7 text-center shadow-2xl shadow-cyan-950/40 backdrop-blur-xl">
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.35em] text-amber-300">Dive into the haul</div>
              <h1 className="text-5xl font-black tracking-tight text-cyan-100 drop-shadow-[0_0_20px_rgba(253,186,116,0.3)]">
                REEF<br /><span className="text-orange-400">STRIKE</span>
              </h1>
              <p className="mt-4 text-sm leading-6 text-slate-300">Aim the brass cannon. Pick your power. Bring home a legendary catch.</p>
              <div className="mt-7 grid gap-3">
                <button onClick={start} className="pointer-events-auto min-h-14 cursor-pointer rounded-2xl bg-amber-400 px-5 text-lg font-black text-slate-950 shadow-lg shadow-amber-950/30 transition hover:bg-amber-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-200 active:scale-95">Start Game</button>
                <button onClick={() => setPhase('howToPlay')} className="pointer-events-auto min-h-14 cursor-pointer rounded-2xl border border-cyan-200/40 bg-cyan-950/40 px-5 text-lg font-bold text-white transition hover:bg-cyan-900/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-200 active:scale-95">How to Play</button>
              </div>
              <p className="mt-6 text-xs text-slate-400">Mobile: Swipe/Tap • PC: Mouse / Space</p>
            </div>
          </motion.section>
        )}
        {phase === 'howToPlay' && (
          <motion.section key="help" {...overlayMotion} className="relative z-20 flex h-full w-full items-center justify-center px-6" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
            <div className="pointer-events-auto w-full max-w-sm rounded-3xl border border-white/15 bg-slate-950/90 p-6 shadow-2xl backdrop-blur-xl">
              <h2 className="text-2xl font-black text-cyan-100">How to Play</h2>
              <div className="mt-5 space-y-3 text-sm leading-6 text-slate-200">
                <p>Move your pointer across the reef to aim. Tap the playfield to fire.</p>
                <p>Power 1 costs 1 coin, power 2 costs 3, and power 3 costs 5.</p>
                <p>Small fish pay 2 coins; medium 6; large 20; rare golden fish 50.</p>
                <p>Defeat the Boss Grouper after 60 seconds for a 200 coin haul.</p>
              </div>
              <div className="mt-6 grid gap-3">
                <button onClick={start} className="min-h-14 cursor-pointer rounded-2xl bg-amber-400 font-black text-slate-950 transition hover:bg-amber-300 active:scale-95">Start Game</button>
                <button onClick={returnToLobby} className="min-h-14 cursor-pointer rounded-2xl border border-white/20 font-bold transition hover:bg-white/10 active:scale-95">Close</button>
              </div>
            </div>
          </motion.section>
        )}
        {phase === 'gameOver' && (
          <motion.section key="result" {...overlayMotion} className="relative z-20 flex h-full w-full items-center justify-center bg-black/75 px-6 backdrop-blur-md" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top))', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
            <div className="pointer-events-auto w-full max-w-sm rounded-3xl border border-white/15 bg-slate-950/90 p-7 text-center shadow-2xl backdrop-blur-xl">
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-amber-300">{result.success ? 'Session Complete' : 'Out of Coins'}</div>
              <div className="mt-3 text-6xl font-black tabular-nums text-cyan-100">{result.finalScore}</div>
              <div className="mt-2 text-sm text-slate-300">Fish caught: {result.fishCaught}</div>
              <p className="mt-5 text-sm text-slate-400">{result.success ? 'The reef rewards a careful shooter.' : 'The tide turned before your haul was complete.'}</p>
              <button onClick={returnToLobby} className="pointer-events-auto mt-7 min-h-14 w-full cursor-pointer rounded-2xl bg-amber-400 px-5 font-black text-slate-950 shadow-lg shadow-amber-950/30 transition hover:bg-amber-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-200 active:scale-95">Restart</button>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
}
export const Page = HomePage;